import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { authedDelete, authedPatch, authedPost } from '@/lib/api';
import { EnvelopeBudget, CreateBudgetData, UpdateBudgetData } from '@/types/budgets';

export const useEnvelopeBudget = (budgetId?: string) => {
  return useQuery({
    queryKey: ['envelopeBudget', budgetId],
    queryFn: async () => {
      if (!budgetId) return null;
      const { data, error } = await supabase
        .from('envelope_budgets')
        .select('*, project:projects!project_id(name), fund:funds!fund_id(fund_name)')
        .eq('id', budgetId)
        .single();
      if (error) throw error;
      return data as EnvelopeBudget & { project: { name: string } | null; fund: { fund_name: string } | null };
    },
    enabled: !!budgetId,
    staleTime: 60_000,
  });
};

export const useEnvelopeBudgets = (organizationId?: string) => {
  return useQuery({
    queryKey: ['envelopeBudgets', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('envelope_budgets')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EnvelopeBudget[];
    },
    enabled: !!organizationId,
    staleTime: 60_000,
  });
};

// Writes go through the Hono backend (Pattern B, /api/v1/budgets — see
// apps/api/src/routes/accounting/budgets.ts); the reads above stay direct for
// now. The API sets organization_id from the URL and created_by from the
// session, defaults status to 'active', and pins every write to the org.
const budgetsPath = (organizationId: string) => `/api/v1/budgets/orgs/${organizationId}/budgets`;

export const useCreateBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: CreateBudgetData }) =>
      authedPost<EnvelopeBudget>(budgetsPath(organizationId), data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['envelopeBudgets', result.organization_id] });
      toast.successTemplates.created('Budget');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

/** Keeps its `{ id, data }` interface; the org comes from OrganizationContext. */
export const useUpdateBudget = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBudgetData }) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      // updated_at is maintained by the set_envelope_budgets_updated_at trigger.
      return authedPatch<EnvelopeBudget>(`${budgetsPath(selectedOrganization.id)}/${id}`, data);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['envelopeBudgets', result.organization_id] });
      queryClient.invalidateQueries({ queryKey: ['envelopeBudget', result.id] });
      toast.successTemplates.updated('Budget');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useDeleteBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, organizationId }: { id: string; organizationId: string }) => {
      await authedDelete<{ deleted: number }>(`${budgetsPath(organizationId)}/${id}`);
      return { organizationId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['envelopeBudgets', result.organizationId] });
      toast.successTemplates.deleted('Budget');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
